import { Timestamp } from "firebase/firestore";
import { Card } from "@/types/v2";
import {
  AutomationToastCategory,
  normalizeToastCategory,
  ToastFieldKey,
} from "@/lib/automationShowToast";
import { firestoreToDateTime } from "@/lib/utils";

export type PendingToast = {
  /** Stable per firing: dedupes across re-renders and Firestore echoes. */
  key: string;
  cardId: string;
  /**
   * Position of this payload within the card's `toastNotifications` array.
   * Two toasts fired on the same card write share an identical `firedAt`,
   * so `index` is what keeps their keys distinct — and what lets
   * `planAutomationToastCycle` look the right entry back up on the live
   * card to snapshot its `expiresAt`.
   */
  index: number;
  headline: string;
  fields: (ToastFieldKey | null)[];
  /** Which sound switch governs this toast; unknown/absent reads as "automation". */
  category: AutomationToastCategory;
};

/**
 * @param {string} cardId - The card the toast belongs to.
 * @param {Date | Timestamp | null | undefined} firedAt - When the automation fired.
 * @param {number} index - The payload's position in the card's
 *   `toastNotifications` array, disambiguating same-`firedAt` payloads.
 * @returns {string} A key unique to this firing.
 */
export function toastKey(
  cardId: string,
  firedAt: Date | Timestamp | null | undefined,
  index: number
): string {
  const at = firestoreToDateTime(firedAt);
  return `${cardId}:${at ? at.getTime() : "unknown"}:${index}`;
}

/**
 * Every unexpired, unseen toast payload across all cards. A card can carry
 * more than one payload (multiple showToast actions on one write) — every
 * entry in `toastNotifications` is considered individually, not just the
 * first.
 * @param {Card[]} cards - All cards in the store.
 * @param {Set<string>} seenKeys - Keys this client has already shown.
 * @param {Date} now - Current wall-clock time.
 * @returns {PendingToast[]} Toasts to show, oldest first.
 */
export function selectPendingToasts(
  cards: Card[],
  seenKeys: Set<string>,
  now: Date
): PendingToast[] {
  const pending: { toast: PendingToast; firedAtMs: number }[] = [];

  for (const card of cards) {
    const notifications = card.toastNotifications;
    if (!notifications || notifications.length === 0) continue;

    notifications.forEach((notification, index) => {
      const firedAt = firestoreToDateTime(notification.firedAt);
      const expiresAt = firestoreToDateTime(notification.expiresAt);
      if (!firedAt || !expiresAt) return;
      if (expiresAt.getTime() <= now.getTime()) return;

      const key = toastKey(card.id, notification.firedAt, index);
      if (seenKeys.has(key)) return;

      pending.push({
        firedAtMs: firedAt.getTime(),
        toast: {
          key,
          cardId: card.id,
          index,
          headline: notification.headline,
          // Clone: a downstream consumer mutating this array must not
          // corrupt the store's copy of the card.
          fields: [...notification.fields],
          category: normalizeToastCategory(notification.category),
        },
      });
    });
  }

  return pending.sort((a, b) => a.firedAtMs - b.firedAtMs).map((p) => p.toast);
}

/**
 * Splits pending toasts into visible rows and an overflow count.
 *
 * `pending` arrives oldest-first (see `selectPendingToasts`), and the
 * accumulator this is normally called on (`automationToastPlan`'s
 * `combined`) preserves that ordering across cycles too — each cycle's
 * newly-pending toasts are appended to the end. So the newest firings are
 * the ones at the tail. An alert-shaped feature should surface the newest
 * activity, not bury it behind "+N more" while the oldest, already-stale
 * firing occupies a visible row — so the tail (newest) is what's shown, and
 * the front (oldest) is what falls into overflow. Visible rows keep their
 * chronological order (oldest-of-the-visible-set on top, newest on
 * bottom), which reads naturally for a stacking toast.
 * @param {PendingToast[]} pending - The pending toasts, oldest first.
 * @param {number} visibleLimit - How many rows the stack renders.
 * @returns {object} The visible rows and the number hidden.
 */
export function planToastStack(
  pending: PendingToast[],
  visibleLimit: number
): { visible: PendingToast[]; overflow: number } {
  const overflow = Math.max(0, pending.length - visibleLimit);
  return {
    visible:
      overflow > 0 ? pending.slice(pending.length - visibleLimit) : pending,
    overflow,
  };
}
